import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StepControls } from '~/common/step-controls';
import { useSelector } from 'react-redux';
import { selectNewSong } from '~/features/dashboard/store/selectors';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useForm } from 'react-hook-form';
import { NamedForm } from '~/features/dashboard/components/add-song/components/named-form';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardHeader, Drawer, Grid, Paper, Switch, Typography } from '@material-ui/core';
import css from './styles.module.scss';
import { newSongFormNextStep } from '~/features/dashboard/store/actions';
import { createNewSongStepFiveThunk } from '~/features/dashboard/store/actions.thunks';
import { useDateFormat } from '~/common/date-format';
import { ExpandMore } from '@material-ui/icons';
import { SongProjectTrack, songsDataTags } from '~/models/music/new-song/newSongResponse.dto';
import { TrackReleaseDrawer, TrackReleaseType } from '~/features/dashboard/components/song-details/coponents/TrackReleaseDrawer';
import classNames from 'classnames';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Avatar from '@material-ui/core/Avatar';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import { useRouteMatch } from 'react-router-dom';
import LibraryMusicOutlinedIcon from '@material-ui/icons/LibraryMusicOutlined';
import { orderBy } from 'lodash';
import fakeUserImagePNG from '~/img/fake-user-image.png';

interface Props {
    prevStep: () => void;
}

export const Step_5: React.FC<Props> = ({ prevStep }) => {
    const { formatDate } = useDateFormat();
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useThunkDispatch();
    let newSongState: any = useSelector(selectNewSong);
    const [songTag, setSongTag] = useState<songsDataTags[]>([]);

    const { handleSubmit, register, setValue, getValues } = useForm({
        defaultValues: {
            tracks: newSongState?.songProjectTrack || [], // TODO: use _.keyBy instead
        },
    });

    const onSubmit = useCallback(
        (data: { tracks: { [isrc: string]: Partial<SongProjectTrack> } }) => {
            setIsLoading(true);
            dispatch(
                createNewSongStepFiveThunk(
                    newSongState?.songProjectTrack.map((t: SongProjectTrack) => {
                        const newTrack: Partial<SongProjectTrack> = data.tracks[t.isrc] || {};
                        return {
                            ...t,
                            ...newTrack,
                            isPublished: newTrack.isPublished ? 1 : 0,
                        };
                    })
                )
            ).finally(() => {
                setIsLoading(false);
                dispatch(newSongFormNextStep());
            });
        },
        [dispatch] // eslint-disable-line
    );

    const match = useRouteMatch<{ creatorId: string }>();
    const creatorId = match.params.creatorId;

    // const [toggle, setToggle] = useState(false);
    const [expandedSub, setExpandedSub] = React.useState<string | false>(false);
    const handleSubAccordion = (trackId: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpandedSub(isExpanded ? trackId : false);
    };

    const keyTracks: SongProjectTrack[] = useMemo(() => (newSongState?.songProjectTrack || []).filter((t: SongProjectTrack) => !!t.isKeyTrack), [newSongState]);

    const [drawerOpen, setDrawerOpen] = useState<boolean | SongProjectTrack>(false);
    const closeDrawer = (data?: TrackReleaseType) => {
        if (data) {
            setValue(`tracks[${data.isrc}].tips`, data.tips);
            setValue(`tracks[${data.isrc}].whereToPublish`, data.whereToPublish);
        }
        setDrawerOpen(false);
    };
    useEffect(() => {
        newSongState && setSongTag(newSongState?.songsDataTags);
    }, [newSongState]); // eslint-disable-line

    return (
        <NamedForm name={newSongState?.gSongName} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
                {!!keyTracks.length ? (
                    keyTracks.map(t => (
                        <Grid item xs={6} key={t.isrc}>
                            <Accordion defaultExpanded className={css.trackItem}>
                                <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`${t.isrc}-content`} id={`${t.isrc}-header`} className={css.title}>
                                    <Typography className={css.heading}>{t.trackTitle}</Typography>
                                </AccordionSummary>
                                <AccordionDetails className={css.details}>
                                    <Paper className={classNames(css.paper, css.contactsContent)}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Box className={css.subheader}>
                                                    {t.trackAudioName && (
                                                        <Typography variant="subtitle2" className={css.trackName}>
                                                            <LibraryMusicOutlinedIcon />
                                                            {t.trackAudioName}
                                                        </Typography>
                                                    )}
                                                    <Box className={classNames('inputcustom-outer', css.switchWrapper)}>
                                                        <Typography component="span" variant="subtitle2" className={css.switchLabel}>
                                                            Publish
                                                        </Typography>
                                                        <Switch
                                                            // disabled={!t.trackAudioName}
                                                            className={css.switch}
                                                            name={`tracks[${t.isrc}].isPublished`}
                                                            type="checkbox"
                                                            inputRef={register}
                                                            defaultChecked={!!t.isPublished}
                                                            onChange={e => {
                                                                setValue(`tracks[${t.isrc}].isPublished`, e.target.checked);
                                                                e.target.checked && setDrawerOpen({ ...t, ...getValues(`tracks[${t.isrc}]`) });
                                                                !e.target.checked && setDrawerOpen(false);
                                                            }}
                                                        />
                                                        <input type="hidden" name={`tracks[${t.isrc}].whereToPublish`} ref={register} />
                                                        <input type="hidden" name={`tracks[${t.isrc}].tips`} ref={register} />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} style={{ paddingBottom: '12px' }}>
                                                <Card elevation={0} className={css.contactCard}>
                                                    <CardHeader
                                                        className={css.contactCardHeader}
                                                        avatar={
                                                            <AvatarGroup>
                                                                <Avatar
                                                                    variant="square"
                                                                    className={css.avatar}
                                                                    alt={t.trackLogoName || ''}
                                                                    src={
                                                                        orderBy(
                                                                            songTag.filter(item => item.isrc === t.isrc),
                                                                            ['id'],
                                                                            ['asc']
                                                                        )[0]?.gSongFilePath || fakeUserImagePNG
                                                                    }
                                                                />
                                                            </AvatarGroup>
                                                        }
                                                        title={
                                                            <Box className={css.avatarInfo}>
                                                                <Typography className={css.key}>Main Artist</Typography>
                                                                <Typography className={classNames(css.value, css.artistName)}>{t.trackArtist}</Typography>
                                                            </Box>
                                                        }
                                                        subheader={
                                                            <Box className={css.avatarInfo}>
                                                                <Typography className={css.key}>Release Date</Typography>
                                                                <Typography className={css.value}>{t.releaseDate ? formatDate(t.releaseDate) : '-'}</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Card>
                                            </Grid>
                                            <Grid item xs={12} className={css.inputSection}>
                                                <TextFieldOutlined
                                                    label="Key Note"
                                                    name={`tracks[${t.isrc}].keyNote`}
                                                    inputRef={register}
                                                    defaultValue={t.keyNote}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Accordion
                                                    className={css.moreInfo}
                                                    expanded={expandedSub === `${t.isrc}-info`}
                                                    onChange={handleSubAccordion(`${t.isrc}-info`)}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMore />}
                                                        aria-controls={`${t.isrc}-info-content`}
                                                        id={`${t.isrc}-info-header`}
                                                        className={css.exapandButton}
                                                    >
                                                        <Typography variant="subtitle2">Additional info</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={2}>
                                                                <Typography variant="subtitle2">Label</Typography>
                                                            </Grid>
                                                            <Grid item xs={10}>
                                                                <Typography variant="body2">{t.trackLabel}</Typography>
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <Typography variant="subtitle2">ISRC</Typography>
                                                            </Grid>
                                                            <Grid item xs={10}>
                                                                <Typography variant="body2">{t.isrc}</Typography>
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <Typography variant="subtitle2">UPC</Typography>
                                                            </Grid>
                                                            <Grid item xs={10}>
                                                                <Typography variant="body2">{t.upcList.join(', ')}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Typography>No Key Tracks available</Typography>
                    </Grid>
                )}
            </Grid>
            <StepControls className={css.nextButton} onPrevClick={prevStep} isSubmitting={isLoading} />
            {newSongState?.gSongRef && (
                <Drawer variant="persistent" anchor={'right'} open={!!drawerOpen} onClose={() => setDrawerOpen(false)} className={css.drawerStyle}>
                    {drawerOpen && <TrackReleaseDrawer projectId={creatorId} data={drawerOpen as SongProjectTrack} close={closeDrawer} />}
                </Drawer>
            )}
        </NamedForm>
    );
};
